import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { SearchInputSmall } from '../common/SearchInputSmall';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CheckPicker, DatePicker, SelectPicker } from 'rsuite';
import clsx from 'clsx';
import { RenderExtraFooter } from '../filter/RenderExtraFooter';
import { FilterComponent } from '../FilterComponent';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { isAfter } from 'date-fns';
import { getFormattedDate, makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { useLocation } from 'react-router';
import { isEqual } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { ButtonComponent } from '../ButtonComponent';
import { WhisperComponent } from '../developers/page-components/WhisperComponent';

export interface IBatchesFiltersParams {
  merchantid?: string;
  mid?: string;
  batchType?: string[];
  partnerId?: string;
  yearAndMonth?: string;
  query?: string;
  showAll?: string;
}

interface Props {
  batchType: string[];
  partnerId: string;
  yearAndMonth: string;
  query: string;
  setBatchType: (value: string[]) => void;
  setPartnerId: (value: string) => void;
  setYearAndMonth: (value: string) => void;
  setQuery: (value: string) => void;
  appliedFiltersState: IBatchesFiltersParams;
  setAppliedFiltersState: (value: IBatchesFiltersParams) => void;
  isBatchesLoading: boolean;
}

export const BatchesFilterSection: FC<Props> = (props) => {
  const {
    batchType, partnerId,yearAndMonth, query,
    setBatchType, setPartnerId, setYearAndMonth, setQuery,
    appliedFiltersState, setAppliedFiltersState,
    isBatchesLoading,
  } = props;

  const initialFilterState: IBatchesFiltersParams = {
    batchType,
    partnerId,
    yearAndMonth,
    query,
  };

  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const picker = useRef(null);
  const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
  const [selectedFiltersState, setSelectedFiltersState] = useState<IBatchesFiltersParams>(initialFilterState);

  const filtersFromUrl: IBatchesFiltersParams = parseSearchParams(search) || {};

  const { merchantState: { merchantsLists, whitelabelPartnersListSelector }} = useMerchants();

  useEffect(() => {
    setSelectedFiltersState(initialFilterState);
  }, [partnerId, yearAndMonth, batchType, query]);

  const allValue = merchantsLists?.batchTypes_v2?.map(item => item.value);
  const handleCheckAll = (value, checked): void => {
    const types = checked ? allValue : [];
    setBatchType(types);
  };

  const handleSave = async (): Promise<void> => picker.current?.close();

  const isSelectedFilters = query || batchType.length || partnerId || yearAndMonth;

  const handleCleanFilter = (): void => {
    if (isSelectedFilters) {
      setBatchType([]);
      setPartnerId('');
      setYearAndMonth(null);
      setQuery('');
    }
  };

  const handleSearch = async (): Promise<void> => {
    setAppliedFiltersState(initialFilterState);

    const params = {
      ...filtersFromUrl,
      ...initialFilterState
    };

    if (params.showAll) {
      delete params.showAll;
    }

    console.log('makeSearchParams', makeSearchParams(params));
    if (!makeSearchParams(params)) {
      params.showAll = 'true';
    }

    navigate(`${pathname}?${makeSearchParams(params)}`);
  };

  return (
    <>
      <div className="card bg-transparent mb-2">
        <div className="card-body px-0 py-0">
          <div className="d-flex block-width application-selector w-100">
            <SearchInputSmall
              placeholder={t('PENDING_PAYOUTS.SEARCH_PLACEHOLDER')}
              setSearchValue={setQuery}
              searchValue={query}
            />

            <ButtonComponent
              additionalClass="btn-sm w-100px d-none d-sm-block ms-10"
              onButtonClick={handleSearch}
              isDisabled={isBatchesLoading || isEqual(initialFilterState, appliedFiltersState)}
              loading={isBatchesLoading}
              cypressAttribute="search-button"
            >
              <>
                {
                  isEqual(selectedFiltersState, appliedFiltersState)
                    ? 'Search'
                    : (
                      <div className="d-flex align-items-center justify-content-center">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        <span className="ms-2 fw-bolder">Refresh</span>
                      </div>
                    )
                }
              </>
            </ButtonComponent>
          </div>
          {
            checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (
              <div className="d-flex align-items-center fc-filter-line  mt-5">
                <FilterComponent value={batchType.length} label={t('PENDING_PAYOUTS.BATCH_TYPES')}
                                 labelPlacement="integrated">
                  <CheckPicker
                    data={merchantsLists?.batchTypes_v2}
                    placeholder={t('PENDING_PAYOUTS.BATCH_TYPES')}
                    className={clsx('fc-selector picker-selector', !batchType.length && 'fc-added-icon')}
                    appearance="subtle"
                    placement="bottomStart"
                    ref={picker}
                    searchable={false}
                    onClean={(): void => setBatchType([])}
                    value={batchType}
                    onChange={(value): void => setBatchType(value)}
                    disabled={isBatchesLoading}
                    data-cy="batch-types-filter"
                    renderMenuItem={(label, item): JSX.Element => {
                      return (
                        <div className="d-flex align-items-center">
                          <div>{label}</div>
                          <WhisperComponent
                            isTooltip
                            whisperText={item?.description}
                            mainText={<FontAwesomeIcon icon={faQuestionCircle} width={12} />}
                            additionalClass="ms-1 cursor-pointer"
                          />
                        </div>
                      );
                    }}
                    renderExtraFooter={(): ReactNode => (
                      <RenderExtraFooter
                        statusFilter={batchType}
                        allValue={allValue}
                        handleCheckAll={handleCheckAll}
                        handleSave={handleSave}
                      />
                    )}
                  />

                </FilterComponent>
                {
                  checkRoles(authUser, CLAIMS.SUPER_ADMIN) && (
                    <FilterComponent value={partnerId} label={t('PENDING_PAYOUTS.BATCH_SENDER')} labelPlacement="integrated">
                      <SelectPicker
                        data={whitelabelPartnersListSelector}
                        appearance="subtle"
                        placement="bottomEnd"
                        placeholder={t('PENDING_PAYOUTS.BATCH_SENDER')}
                        onSelect={(pid): void => setPartnerId(pid)}
                        className={clsx('fc-selector timezone-selector', partnerId ? 'fc-selector-cleanable' : 'fc-added-icon')}
                        onClean={(): void => setPartnerId('')}
                        value={partnerId}
                        disabled={isBatchesLoading}
                        data-cy="pid-filter"
                      />
                    </FilterComponent>
                  )
                }

                <FilterComponent value={yearAndMonth} label="Date" labelPlacement="integrated">
                  <div className={clsx('fc-datepicker fc-datepicker-month', yearAndMonth && 'fc-added-icon')}>
                    <DatePicker
                      placeholder="Date"
                      placement="bottomEnd"
                      onChange={(value): void => setYearAndMonth(getFormattedDate(value, 'yyyy-MM'))}
                      onClean={(): void => setYearAndMonth(null)}
                      shouldDisableDate={(date): boolean => isAfter(date, new Date())}
                      value={yearAndMonth ? new Date(yearAndMonth) : null}
                      format="MMM yyyy"
                      limitEndYear={2}
                      limitStartYear={5}
                      disabled={isBatchesLoading}
                    />
                  </div>
                </FilterComponent>

                <div
                  className={clsx(
                    'text-danger text-decoration-underline',
                    isSelectedFilters && !isBatchesLoading ? 'cursor-pointer' : 'cursor-not-allowed')
                  }
                  onClick={(): void => !isBatchesLoading && handleCleanFilter()}
                >
                  Clear filters
                </div>
              </div>
            )}
        </div>
      </div>
    </>
  );
};